import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, RotateCcw, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";
import { retryStandardAgreement, StandardAgreement } from "../../services/StandardAgreements";

/** Ready · N clauses / Preparing… / Couldn't be read */
export const StandardChip: React.FC<{ standard: StandardAgreement; className?: string }> = ({ standard, className }) => {
  const { t } = useI18n();
  const prep = t.comparisons.prep;
  const v = standard.currentVersion;
  const base = "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium";
  if (!v || v.status === "failed") {
    return (
      <span
        className={cn(base, "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200", className)}
        title={v?.error ?? undefined}
        data-status="failed"
      >
        <TriangleAlert className="h-3 w-3" aria-hidden />
        {prep.failed}
      </span>
    );
  }
  if (v.status === "ready") {
    return (
      <span className={cn(base, "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200", className)} data-status="ready">
        <Check className="h-3 w-3" aria-hidden />
        {prep.ready(v.clauseCount)}
      </span>
    );
  }
  return (
    <span className={cn(base, "bg-muted text-muted-foreground", className)} data-status="preparing">
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      {prep.preparing}
    </span>
  );
};

/** Re-queues preparation of a failed standard; the list refreshes and polls again */
export const RetryButton: React.FC<{ standard: StandardAgreement; onDone: () => Promise<void> | void }> = ({ standard, onDone }) => {
  const { t } = useI18n();
  const c = t.comparisons;
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      const res = await retryStandardAgreement(standard.id);
      if (!res.success) toast.error(c.cp.retryFailed);
      await onDone();
    } catch {
      toast.error(c.cp.retryFailed);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={busy} onClick={() => void retry()} data-testid="retry-standard">
      {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-1 h-3 w-3" />}
      {c.cp.retry}
    </Button>
  );
};
